import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, InputObservable } from '@y42/angular/util';
import { TestResultStatus } from '@y42/data-test';
import { isNullish } from '@y42/util/nullish';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataTestResultToIcon } from '../../../state';

const ComponentIconsAndLabel: Record<
  keyof typeof DataTestResultToIcon | 'noData',
  {
    label: string;
    icon: string;
  }
> = {
  ...DataTestResultToIcon,
  noData: {
    label: 'Status will be available after job execution',
    icon: `assets/images/data-test-status-icons/data-test-no-runs.svg`,
  },
};

@UntilDestroy()
@Component({
  selector: 'y42-data-test-result-icon[status]',
  templateUrl: './data-test-result-icon.component.html',
  styleUrls: ['./data-test-result-icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataTestResultIconComponent {
  @Input()
  @InputObservable()
  status!: TestResultStatus | null;
  status$!: Observable<TestResultStatus | null>;

  @Input()
  @InputObservable()
  errorMessages?: string[] | string | null = null;
  errorMessages$!: Observable<string[] | string | null>;

  private readonly errorTooltipText$: Observable<string> = this.errorMessages$.pipe(
    map((errorMessages) => {
      const errorLabelAndIcon = ComponentIconsAndLabel[TestResultStatus.Error];
      if (isNullish(errorMessages)) {
        return errorLabelAndIcon.label;
      }

      if (typeof errorMessages === 'string') {
        return errorMessages;
      }

      return errorMessages.length > 0 ? errorMessages.join('; ') : errorLabelAndIcon.label;
    }),
  );

  private readonly labelAndIcon$: Observable<{ label: string; icon: string }> = combineLatest([
    this.status$,
    this.errorTooltipText$,
  ]).pipe(
    map(([status, errorTooltip]): { label: string; icon: string } => {
      if (isNullish(status)) {
        return ComponentIconsAndLabel.noData;
      }
      switch (status) {
        case TestResultStatus.Success:
          return ComponentIconsAndLabel[TestResultStatus.Success];
        case TestResultStatus.Error:
          return {
            label: errorTooltip,
            icon: ComponentIconsAndLabel[TestResultStatus.Error].icon,
          };
      }
    }),
  );

  readonly state = connectState(this, {
    labelAndIcon: this.labelAndIcon$,
  });
}
